import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation } from "react-router-dom";
import Homepage from "./pages/Homepage";
import SideMenuOverlay from "./components/SideMenuOverlay";
import ScrollToTop from "./components/ScrollToTop";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Media from "./pages/media/index.js";
import Piston from './pages/media/Piston';
import SingleTurbine from './pages/media/SingleTurbine';
import MultiTurbine from './pages/media/MultiTurbine';
import TopNavBar from "./components/TopNavBar";
import Login from './pages/Login';
import Portal from "./pages/BrokerPortal";
import AdminHome from "./pages/AdminHome"; 
import AdminProjectsPage from "./pages/AdminProjectsPage";
import AdminProjectDetail from "./pages/AdminProjectDetail";
import AdminProfile from "./pages/AdminProfile";
import BrokerIntake from "./pages/BrokerIntake"; 
import { supabase } from "./supabaseClient";
import { initGA, trackPageView } from "./utils/analytics";
import UpdatePassword from "./components/UpdatePassword";
import LoginAuth from "./components/loginauth";
import AuthVerify from "./components/AuthVerify";
import BrokerProjectsPage from "./components/BrokerProjectsPage.js";
import BrokerProfile from "./components/BrokerProfile";
import BrokerProjectDetailPage from "./components/BrokerProjectDetailPage";
import AdminRoute from "./components/AdminRoute";
import BrokerRoute from "./components/BrokerRoute";
import ChoosePortal from "./pages/ChoosePortal";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import SmsCompliance from "./pages/SmsCompliance";
import AcceptInvite from "./pages/AcceptInvite";
import { isPortalBetaHost } from "./utils/portalHost";
import { traceAsync } from "./utils/perfTrace";

const EmailPreview = process.env.NODE_ENV !== "production"
  ? lazy(() => import("./pages/EmailPreview"))
  : null;

function TrackPageView() {
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const [showTopNav, setShowTopNav] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const portalBetaHost = isPortalBetaHost();
  const HIDE_TOPNAV_PREFIXES = ["/portal", "/choose-portal", "/accept-invite", "/brokerprojects", "/projects", "/intake", "/admin", "/profile", "/terms", "/privacy", "/sms-compliance"];
  const shouldShowNav = !portalBetaHost && !HIDE_TOPNAV_PREFIXES.some(p => location.pathname.startsWith(p));
  const marketingRoute = (element) => (portalBetaHost ? <Navigate to="/login" replace /> : element);


  useEffect(() => {
    const restoreSession = async () => {
      const { data, error } = await traceAsync(
        "app.restoreSession",
        () => supabase.auth.getSession(),
        {}
      );
      if (error) {
        console.error("Session restoration error:", error.message);
      } else {
        const session = data?.session;
console.log("Session restored:", session);
      }
    };
    restoreSession();
  }, []);

  // Handle password reset flow
useEffect(() => {
  const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
    if (event === "PASSWORD_RECOVERY") {
      console.log("🔐 Password recovery detected — redirecting to /update-password");
      window.location.replace("/update-password");
    }
  });

  return () => {
    authListener?.subscription.unsubscribe();
  };
}, []);


  useEffect(() => {
    const handleScroll = () => {
      const hero = document.querySelector("#hero");
      const heroHeight = hero?.offsetHeight || window.innerHeight;
      setShowTopNav(window.scrollY > heroHeight - 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHomepage = location.pathname === "/";
// Close menu when navigating away from homepage
useEffect(() => {
  if (!isHomepage) {
    setMenuOpen(false);
  }
}, [location.pathname, isHomepage]);

  return (
    <>
      <ScrollToTop />
      <TrackPageView />

      {/*  Top NavBar: Only on non-dashboard routes */}
{shouldShowNav && (
  <>
    {/* TopNavBar: always visible on mobile */}
    <div className="block md:hidden fixed top-0 left-0 w-full z-50">
      <TopNavBar isMenuOpen={menuOpen} toggleMenu={() => setMenuOpen(!menuOpen)} />
    </div>

    {/* TopNavBar: fades in on scroll for desktop */}
    {location.pathname === "/" && (
      <div
        className={`hidden md:block fixed top-0 left-0 w-full z-50 transition-all duration-300 ease-in-out transform ${
          showTopNav ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        <TopNavBar isMenuOpen={menuOpen} toggleMenu={() => setMenuOpen(!menuOpen)} />
      </div>
    )}

    {/* TopNavBar: always visible on non-home routes */}
    {location.pathname !== "/" && (
      <div className="hidden md:block fixed top-0 left-0 w-full z-50">
        <TopNavBar isMenuOpen={menuOpen} toggleMenu={() => setMenuOpen(!menuOpen)} />
      </div>
    )}
  </>
)}



      {/* Side Menu Overlay - desktop only, homepage only */}
      {isHomepage && (
        <div className="hidden md:block">
          <SideMenuOverlay isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
        </div>
      )}

      {/*  Main Content */}
      <div className="relative bg-[#FAF9F6] text-[#1A1A1A]">
        <Routes>
          <Route path="/" element={marketingRoute(<Homepage isMenuOpen={menuOpen} toggleMenu={() => setMenuOpen(!menuOpen)} />)} />
          <Route path="/about" element={marketingRoute(<About />)} />
          <Route path="/services" element={marketingRoute(<Services />)} />
          <Route path="/contact" element={marketingRoute(<Contact />)} />
          <Route path="/media" element={marketingRoute(<Media />)} />
          <Route path="/media/piston" element={marketingRoute(<Piston />)} />
          <Route path="/media/singleturbine" element={marketingRoute(<SingleTurbine />)} />
          <Route path="/media/multiturbine" element={marketingRoute(<MultiTurbine />)} />
          <Route path="/login" element={<Login />} />
          <Route path="/choose-portal" element={<ChoosePortal />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/sms-compliance" element={<SmsCompliance />} />
          <Route path="/portal" element={<BrokerRoute><Portal /></BrokerRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminHome /></AdminRoute>} />
          <Route path="/admin/projects" element={<AdminRoute><AdminProjectsPage /></AdminRoute>} />
          <Route path="/admin/projects/:id" element={<AdminRoute><AdminProjectDetail /></AdminRoute>} />
          <Route path="/admin/profile" element={<AdminRoute><AdminProfile /></AdminRoute>} />
          <Route path="/admin/intakes" element={<AdminRoute><AdminProjectsPage /></AdminRoute>} />
          <Route path="/admin/tracker" element={<AdminRoute><AdminProjectsPage /></AdminRoute>} />
          <Route path="/intake" element={<BrokerRoute><BrokerIntake /></BrokerRoute>} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/auth/callback" element={<LoginAuth />} />
          <Route path="/auth/verify" element={<AuthVerify />} />
          <Route path="/brokerprojects" element={<BrokerRoute><BrokerProjectsPage /></BrokerRoute>} />
          <Route path="/projects/:tail" element={<BrokerRoute><BrokerProjectDetailPage /></BrokerRoute>} />
          <Route path="/profile" element={<BrokerRoute><BrokerProfile /></BrokerRoute>} />
          {EmailPreview && (
            <Route
              path="/email-preview"
              element={(
                <Suspense fallback={<div className="min-h-screen bg-white p-8">Loading email preview...</div>}>
                  <EmailPreview />
                </Suspense>
              )}
            />
          )}
        </Routes>
      </div>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
